/**
 * Error Tracking Module
 *
 * Provides error monitoring and reporting via Sentry.
 * Only initializes in production when DSN is configured.
 */

import * as Sentry from '@sentry/browser';
import type { ErrorContext } from '../types';

let initialized = false;

/**
 * Initialize Sentry error tracking
 * Only runs in production with a valid DSN
 */
export function initErrorTracking(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization in development or without DSN
  if (import.meta.env.DEV || !dsn) {
    console.info('Error tracking disabled (development mode or no DSN)');
    return;
  }

  if (initialized) {
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: `mecell-maps@${import.meta.env.VITE_APP_VERSION || '2.0.0'}`,

      // Only send errors, not all transactions
      tracesSampleRate: 0,

      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,

        // Network errors that are expected
        'Network request failed',
        'Failed to fetch',
        'Load failed',

        // User-caused errors
        'ResizeObserver loop',
      ],

      // Don't send PII
      beforeSend(event) {
        // Remove any potential user data
        if (event.user) {
          delete event.user.ip_address;
          delete event.user.email;
        }
        return event;
      },

      // Add useful context
      initialScope: {
        tags: {
          app: 'mecell-maps',
        },
      },
    });

    initialized = true;
    console.info('Error tracking initialized');
  } catch (error) {
    console.warn('Failed to initialize error tracking:', error);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error | unknown, context: ErrorContext = {}): void {
  if (!initialized) {
    console.error('Uncaptured error:', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    if (context.level) {
      scope.setLevel(context.level);
    }

    Sentry.captureException(error);
  });
}

/**
 * Capture a message manually
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (!initialized) {
    console.log(`[${level}]`, message);
    return;
  }

  Sentry.captureMessage(message, level);
}

/**
 * Set user context (anonymized)
 */
export function setUser(user: { id: string }): void {
  if (!initialized) return;

  Sentry.setUser({
    id: user.id,
    // Don't include email or IP
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: {
  category?: string;
  message: string;
  level?: 'info' | 'warning' | 'error' | 'debug';
  data?: Record<string, unknown>;
}): void {
  if (!initialized) return;

  Sentry.addBreadcrumb({
    category: breadcrumb.category || 'app',
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
  });
}

/**
 * Set extra context data
 */
export function setContext(key: string, value: Record<string, unknown>): void {
  if (!initialized) return;

  Sentry.setContext(key, value);
}

/**
 * Create a global error handler
 */
export function setupGlobalErrorHandler(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    captureException(event.reason || new Error('Unhandled rejection'), {
      tags: { type: 'unhandledrejection' },
    });
  });

  // Handle global errors
  window.addEventListener('error', (event: ErrorEvent) => {
    // Ignore errors from cross-origin scripts
    if (event.message === 'Script error.' && !event.filename) {
      return;
    }

    captureException(event.error || new Error(event.message), {
      tags: { type: 'global-error' },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}

/**
 * Check if error tracking is initialized
 */
export function isInitialized(): boolean {
  return initialized;
}
